import React, { useState } from "react";
import Card from "../../../components/ui/Card";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import { Send, ArrowLeft, Image, MessageSquare } from "lucide-react";

const MessagesPage = () => {
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [message, setMessage] = useState("");
  
  const conversations = [
    {
      id: 1,
      creatorName: "Gigi Kiya",
      creatorAvatar: "https://i.pravatar.cc/50?u=gigi",
      lastMessage: "Thank you for your support!",
      timestamp: "2h ago",
      unread: 2
    },
    {
      id: 2,
      creatorName: "DJ Habesha",
      creatorAvatar: "https://i.pravatar.cc/50?u=dj",
      lastMessage: "Check out my new mix!",
      timestamp: "1d ago",
      unread: 0
    }
  ];

  const messages = selectedConversation ? [
    {
      id: 1,
      sender: "me",
      text: "Hi! Love your latest track!",
      timestamp: "3h ago"
    },
    {
      id: 2,
      sender: "creator",
      text: "Thank you so much for your support! ❤️",
      timestamp: "2h ago"
    },
    {
      id: 3,
      sender: "me",
      text: "When's the next concert?",
      timestamp: "2h ago"
    }
  ] : [];

  const handleSend = () => {
    if (!message.trim()) return;
    setMessage("");
  };

  const [searchTerm, setSearchTerm] = useState("");

  const filteredConversations = conversations.filter(conv => 
    conv.creatorName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    conv.lastMessage.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!selectedConversation) {
    return (
      <div className="flex flex-col h-screen bg-slate-50">
        <div className="p-4 bg-white border-b">
          <h1 className="text-xl font-bold text-slate-800">Messages</h1>
          <p className="text-sm text-slate-500">Chat with your favorite creators</p>
          <div className="mt-4">
            <Input 
              placeholder="Search messages..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {filteredConversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-400">
              <MessageSquare size={48} className="mb-4 opacity-50" />
              <p>No conversations found.</p>
            </div>
          ) : (
            filteredConversations.map(conv => (
              <Card
                key={conv.id}
                onClick={() => setSelectedConversation(conv)}
                className="mb-3 p-4 cursor-pointer hover:bg-slate-50 transition"
              >
                <div className="flex items-center">
                  <img
                    src={conv.creatorAvatar}
                    alt={conv.creatorName}
                    className="w-12 h-12 rounded-full mr-3 flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start mb-1">
                      <p className="font-semibold text-slate-800 truncate">{conv.creatorName}</p>
                      <span className="text-xs text-slate-500 flex-shrink-0 ml-2">{conv.timestamp}</span>
                    </div>
                    <p className="text-sm text-slate-600 truncate">{conv.lastMessage}</p>
                  </div>
                  {conv.unread > 0 && (
                    <div className="ml-2 bg-primary text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0">
                      {conv.unread}
                    </div>
                  )}
                </div>
              </Card>
            ))
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[100dvh] bg-white">
      {/* Header */}
      <div className="p-4 border-b flex items-center bg-white flex-shrink-0 z-10">
        <button
          onClick={() => setSelectedConversation(null)}
          className="mr-3 p-2 hover:bg-slate-100 rounded-lg"
        >
          <ArrowLeft size={20} />
        </button>
        <img
          src={selectedConversation.creatorAvatar}
          alt={selectedConversation.creatorName}
          className="w-10 h-10 rounded-full mr-3 flex-shrink-0"
        />
        <div>
          <p className="font-semibold text-slate-800">{selectedConversation.creatorName}</p>
          <p className="text-xs text-green-500">● Online</p>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50 pb-20">
        {messages.map(msg => (
          <div
            key={msg.id}
            className={`flex ${msg.sender === 'me' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[75%] rounded-2xl px-4 py-2 ${
                msg.sender === 'me'
                  ? 'bg-primary text-white'
                  : 'bg-white text-slate-800 border border-slate-200'
              }`}
            >
              <p className="break-words">{msg.text}</p>
              <p className={`text-xs mt-1 ${msg.sender === 'me' ? 'text-purple-200' : 'text-slate-500'}`}>
                {msg.timestamp}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Input - Sticky at bottom */}
      <div className="p-4 border-t bg-white flex-shrink-0 fixed bottom-0 left-0 right-0 w-full z-20 md:static md:w-auto">
        <div className="flex items-center space-x-2 max-w-4xl mx-auto">
          <button className="p-2 text-slate-500 hover:text-slate-700 flex-shrink-0">
            <Image size={20} />
          </button>
          <Input
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Type a message..."
            onKeyPress={(e) => e.key === 'Enter' && handleSend()}
            className="flex-1"
          />
          <Button onClick={handleSend} disabled={!message.trim()} className="flex-shrink-0">
            <Send size={18} />
          </Button>
        </div>
        <p className="text-xs text-slate-500 mt-2 text-center hidden sm:block">
          💡 Free users: 5 messages/month. Subscribe for unlimited!
        </p>
      </div>
    </div>
  );
};

export default MessagesPage;
